import { clsx, type ClassValue } from "clsx";
import convert, { type Unit } from "convert";
import { twMerge } from "tailwind-merge";
import * as z from "zod";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function compareAsJSON<T>(a: T, b: T): boolean {
	try {
		return JSON.stringify(a) === JSON.stringify(b);
	} catch (error) {
		console.error("Error comparing objects as JSON:", error);
		return false;
	}
}

export function isInstantBetween(
	compared: Temporal.Instant | Temporal.ZonedDateTime,
	older: Temporal.Instant | Temporal.ZonedDateTime | null,
	newer: Temporal.Instant | Temporal.ZonedDateTime | null,
): boolean {
	const duration1 = older ? compared.since(older.toString()) : { sign: -1 };
	const duration2 = newer ? compared.since(newer.toString()) : { sign: 1 };
	return duration1.sign >= 0 && duration2.sign <= 0;
}

const bestFormatterInt = new Intl.NumberFormat("en-US", {
	maximumFractionDigits: 0,
});
const bestFormatterFloat = new Intl.NumberFormat(undefined, {
	maximumFractionDigits: 2,
});
export function convertToBest(value: number, fromUnit: Unit | "€") {
	if (fromUnit === "€") {
		if (value >= 1000) {
			const formattedNumber = bestFormatterInt.format(value).replace(/,/g, " ");
			return formattedNumber + "€";
		} else {
			const formattedNumber = bestFormatterFloat.format(value);
			return formattedNumber + "€";
		}
	}

	const converted = convert(value, fromUnit).to("best");
	if (converted.quantity >= 1000) {
		const formattedNumber = bestFormatterInt.format(converted.quantity).replace(/,/g, " ");
		return formattedNumber + converted.unit;
	} else {
		const formattedNumber = bestFormatterFloat.format(converted.quantity);
		return formattedNumber + converted.unit;
	}
}

export function minMax(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

export function DefaultValuesSchema<T extends z.ZodType>(schema: T): T {
	if (schema instanceof z.ZodString || schema.type == "string") {
		return schema.or(z.literal("")) as unknown as T;
	}

	if (schema instanceof z.ZodObject) {
		const shape = schema.shape;

		const newShape = Object.fromEntries(
			Object.entries(shape).map(([key, value]) => [
				key,
				DefaultValuesSchema(value),
			]),
		);

		return schema.extend(newShape) as unknown as T;
	}


	return schema;
}